import { error, warn } from '../logger/Logger';
import type { Cart } from '../models/Cart';
import type {
    PageViewInteraction,
    PageViewInteractionLocation
} from '../models/customer-session/PageViewInteraction';
import { PageInfo } from '../models/PageInfo';
import type { Product } from '../models/Product';
import type { OmitSessionId } from '../services/ActiveHelpService';

export function getPageInfo(): PageInfo {
    const type = getFirstAttributeValueOrNull('data-bri-page-type') as
        | PageInfo['type']
        | null;
    const partialPageInfo: Pick<PageInfo, 'url' | 'locale' | 'currency'> = {
        url: window.location.href,
        locale: (
            getFirstAttributeValueOrNull('data-bri-page-locale') || 'es'
        ).trim(),
        currency: (
            getFirstAttributeValueOrNull('data-bri-page-currency') || 'EUR'
        ).trim()
    };
    try {
        switch (type) {
            case 'product':
                return {
                    ...partialPageInfo,
                    type,
                    productId: getFirstAttributeValue('data-bri-product-id')
                };
            case 'collection':
                return {
                    ...partialPageInfo,
                    type,
                    collectionId: getFirstAttributeValue(
                        'data-bri-collection-id'
                    )
                };
            case 'order':
                return {
                    ...partialPageInfo,
                    type,
                    orderId: getFirstAttributeValue('data-bri-order-id')
                };
            case 'search':
                return {
                    ...partialPageInfo,
                    type,
                    searchText: getFirstAttributeValue('data-bri-search-text')
                };
            case 'policy':
                return {
                    ...partialPageInfo,
                    type,
                    policyTitle: getFirstAttributeValue('data-bri-policy-title')
                };
            case 'article':
                return {
                    ...partialPageInfo,
                    type,
                    articleTitle: getFirstAttributeValue(
                        'data-bri-article-title'
                    )
                };
            case 'blog':
                return {
                    ...partialPageInfo,
                    type,
                    blogTitle: getFirstAttributeValue('data-bri-blog-title')
                };
            case 'index':
            case 'cart':
            case 'checkout':
            case 'list-collections':
            case 'contact':
            case 'faq':
            case 'profile':
            case 'orders':
            case 'login':
            case 'register':
            case 'not-found':
            case 'general':
                return {
                    ...partialPageInfo,
                    type
                };
            case null:
                warn(
                    `Not found data-bri-page-type attribute. Using "general" as page type.`
                );
                return {
                    ...partialPageInfo,
                    type: 'general'
                };
            default:
                throw new Error(
                    `Unknown attribute value data-bri-page-type: "${type}"`
                );
        }
    } catch (err) {
        error(
            `Invalid data-bri-page-type "${type}" configuration. Using "general" as page type.`,
            err
        );
        return {
            ...partialPageInfo,
            type: 'general'
        };
    }
}

function getFirstAttributeValueOrNull(attributeName: string): string | null {
    const element = document.querySelector(`[${attributeName}]`);
    let value: string | null = null;
    if (element) {
        value = element.getAttribute(attributeName);
    }
    return value;
}

function getFirstAttributeValue(attributeName: string): string {
    const value = getFirstAttributeValueOrNull(attributeName);
    if (typeof value === 'string' && value) {
        return value;
    }
    throw new Error(
        `Required attribute named "${attributeName}" not found in DOM.`
    );
}

function getPageViewInteractionLocation(
    location?: string
): PageViewInteractionLocation {
    const url = location ? new URL(location) : ({} as unknown as URL);
    return {
        hash: url.hash || '',
        host: url.host || '',
        hostname: url.hostname || '',
        href: url.href || '',
        origin: url.origin || '',
        pathname: url.pathname || '',
        port: url.port || '',
        protocol: url.protocol || '',
        search: url.search || '',
        searchParams: Array.from(url.searchParams?.entries() || []).map(
            ([key, value]) => ({ key, value })
        )
    };
}

export function getInteractionPageViewFromPageInfo({
    pageInfo,
    product
}: {
    pageInfo: PageInfo;
    product?: Product;
}): OmitSessionId<PageViewInteraction> {
    return {
        type: 'page-view',
        date: new Date(),
        title: document.title,
        location: getPageViewInteractionLocation(document.location.href),
        referrer: getPageViewInteractionLocation(document.referrer),
        ...(pageInfo.type === 'product'
            ? {
                  pageType: 'product',
                  product: product as Product
              }
            : pageInfo.type === 'cart'
              ? {
                    pageType: 'cart',
                    cart: null as unknown as Cart // TODO: implement me
                }
              : pageInfo.type === 'search'
                ? {
                      pageType: 'search',
                      searchText: pageInfo.searchText
                  }
                : pageInfo.type === 'policy'
                  ? {
                        pageType: 'policy',
                        policyTitle: pageInfo.policyTitle
                    }
                  : pageInfo.type === 'article'
                    ? {
                          pageType: 'article',
                          articleTitle: pageInfo.articleTitle
                      }
                    : pageInfo.type === 'blog'
                      ? {
                            pageType: 'blog',
                            blogTitle: pageInfo.blogTitle
                        }
                      : {
                            pageType: pageInfo.type as Exclude<
                                PageInfo['type'],
                                | 'product'
                                | 'cart'
                                | 'search'
                                | 'policy'
                                | 'article'
                                | 'blog'
                            >
                        })
    };
}
